import React from 'react';
import { LeadCaptureForm } from '../components/LeadCaptureForm';
import { Check, Zap, Shield, Clock, Target } from 'lucide-react';
export function LeadCapturePage() {
  const benefits = ['Personalized training program designed for your goals', 'Professional kickboxing and fitness coaching', 'Flexible scheduling in central Warsaw', 'English-speaking training environment'];
  const features = ['Initial fitness assessment and goal setting', 'Customized training plan tailored to you', 'Proper technique instruction to prevent injury', 'Ongoing support and program adjustments'];
  return <main className="flex-grow bg-black" data-id="element-195">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-id="element-196">
        <div className="max-w-5xl mx-auto text-center space-y-8" data-id="element-197">
          <div className="inline-flex items-center gap-2 bg-[#D4FF00]/10 border border-[#D4FF00]/30 rounded-full px-6 py-2 text-[#D4FF00] font-medium mb-4" data-id="element-198">
            <Zap className="w-4 h-4" data-id="element-199" />
            Limited Availability
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight" data-id="element-200">
            Ready To Transform Your{' '}
            <span className="text-[#D4FF00]" data-id="element-201">Body & Mind</span>?
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-202">
            Apply below for 1-on-1 kickboxing and fitness training in Warsaw.
            Get your discounted first session and free consultation call.
          </p>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="py-12 bg-gradient-to-r from-gray-900 to-black border-y border-gray-800" data-id="element-203">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-204">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-205">
            {benefits.map((benefit, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-206">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4FF00] flex items-center justify-center mt-1" data-id="element-207">
                  <Check className="w-4 h-4 text-black" strokeWidth={3} data-id="element-208" />
                </div>
                <span className="text-gray-200 text-sm leading-relaxed" data-id="element-209">
                  {benefit}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-210">
        <div className="max-w-6xl mx-auto" data-id="element-211">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-212">
            {/* Left Column - Content */}
            <div className="space-y-8" data-id="element-213">
              <div data-id="element-214">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-215">
                  What Makes This Different
                </h2>
                <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-216">
                  Unlike typical gym classes where you're just another number,
                  my 1-on-1 training sessions are completely focused on your
                  individual progress. We work together on technique,
                  conditioning, and building the mindset you need to succeed.
                </p>
                <p className="text-gray-300 leading-relaxed" data-id="element-217">
                  Whether you want to lose weight, build strength, learn
                  self-defense, or simply feel more confident in your body, I'll
                  create a personalized plan that gets you there. Training is
                  conducted in English in central Warsaw with flexible
                  scheduling.
                </p>
              </div>

              {/* Feature Highlights */}
              <div className="space-y-4" data-id="element-218">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-219">
                  What You'll Get:
                </h3>
                <div className="space-y-3" data-id="element-220">
                  {features.map((feature, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-221">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4FF00]/20 flex items-center justify-center mt-1" data-id="element-222">
                        <Check className="w-4 h-4 text-[#D4FF00]" strokeWidth={3} data-id="element-223" />
                      </div>
                      <span className="text-gray-300" data-id="element-224">{feature}</span>
                    </div>)}
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-3 gap-4 pt-4" data-id="element-225">
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 text-center" data-id="element-226">
                  <Shield className="w-6 h-6 text-[#D4FF00] mx-auto mb-2" data-id="element-227" />
                  <p className="text-xs text-gray-400" data-id="element-228">Professional Coach</p>
                </div>
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 text-center" data-id="element-229">
                  <Clock className="w-6 h-6 text-[#D4FF00] mx-auto mb-2" data-id="element-230" />
                  <p className="text-xs text-gray-400" data-id="element-231">Flexible Schedule</p>
                </div>
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 text-center" data-id="element-232">
                  <Target className="w-6 h-6 text-[#D4FF00] mx-auto mb-2" data-id="element-233" />
                  <p className="text-xs text-gray-400" data-id="element-234">Results Focused</p>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="lg:sticky lg:top-24" data-id="element-235">
              <div className="mb-6" data-id="element-236">
                <h2 className="text-2xl font-bold text-white mb-2" data-id="element-237">
                  Apply For Training
                </h2>
                <p className="text-gray-400" data-id="element-238">
                  Fill out the form and I'll get back to you within 24 hours to
                  schedule your free consultation call.
                </p>
              </div>
              <LeadCaptureForm submitEmail="contact@szulcaleks.com" data-id="element-239" />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black to-gray-900 border-t border-gray-800" data-id="element-240">
        <div className="max-w-4xl mx-auto" data-id="element-241">
          <h2 className="text-3xl font-bold text-white text-center mb-12" data-id="element-242">
            How It Works
          </h2>

          <div className="space-y-6" data-id="element-243">
            {[{
            step: '1',
            title: 'Submit Your Application',
            description: 'Fill out the form above with your information and fitness goals.'
          }, {
            step: '2',
            title: 'Free Consultation Call',
            description: "We'll schedule a call to discuss your goals, experience level, and answer any questions."
          }, {
            step: '3',
            title: 'Book Your First Session',
            description: "If we're a good fit, book your discounted first training session and start your transformation."
          }].map((item, idx) => <div key={idx} className="flex gap-6 bg-gray-900/50 border border-gray-800 rounded-xl p-6 hover:border-[#D4FF00]/30 transition-colors" data-id="element-244">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#D4FF00] flex items-center justify-center text-black font-bold text-xl" data-id="element-245">
                  {item.step}
                </div>
                <div data-id="element-246">
                  <h3 className="text-xl font-bold text-white mb-2" data-id="element-247">
                    {item.title}
                  </h3>
                  <p className="text-gray-300" data-id="element-248">{item.description}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-gray-800" data-id="element-249">
        <div className="max-w-4xl mx-auto" data-id="element-250">
          <h2 className="text-3xl font-bold text-white text-center mb-12" data-id="element-251">
            Common Questions
          </h2>

          <div className="space-y-6" data-id="element-252">
            {[{
            q: 'Do I need prior experience?',
            a: 'No! I work with complete beginners through advanced athletes. Your training will be tailored to your current level.'
          }, {
            q: 'Where are sessions held?',
            a: 'Training takes place in central Warsaw at a professional gym facility. Exact location details provided after booking.'
          }, {
            q: 'How long are sessions?',
            a: 'Standard sessions are 60 minutes. We can adjust based on your goals and schedule.'
          }, {
            q: 'What should I bring?',
            a: 'Just comfortable workout clothes and water. All equipment (gloves, pads, etc.) is provided.'
          }].map((faq, idx) => <div key={idx} className="bg-gray-900/50 border border-gray-800 rounded-xl p-6" data-id="element-253">
                <h3 className="text-lg font-bold text-white mb-3" data-id="element-254">{faq.q}</h3>
                <p className="text-gray-300" data-id="element-255">{faq.a}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-gray-900 via-black to-gray-900 border-t border-gray-800" data-id="element-256">
        <div className="max-w-4xl mx-auto text-center space-y-6" data-id="element-257">
          <h2 className="text-3xl md:text-4xl font-bold text-white" data-id="element-258">
            Ready To Get Started?
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-259">
            Scroll up and fill out the application form. I'll reach out within
            24 hours to schedule your free consultation.
          </p>
          <button onClick={() => window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })} className="bg-[#D4FF00] text-black px-8 py-4 rounded-lg text-base font-bold uppercase tracking-wide hover:bg-[#c4ef00] transition-all hover:scale-105 hover:shadow-lg hover:shadow-[#D4FF00]/30" data-id="element-260">
            Apply Now
          </button>
        </div>
      </section>
    </main>;
}